import {
  Check,
  GraduationCap,
  Laptop,
  LogOut,
  School,
  ShieldCheck,
  Smartphone,
  UserRound,
  Users,
  WifiOff,
} from "lucide-react";
import { useEffect, useState } from "react";
import {
  Avatar,
  Button,
  InlineNotice,
  PageHeader,
  SchoolMark,
  StateBadge,
} from "../components/ui";
import { useApp } from "../state/AppContext";
import type { Role } from "../types";
import "../styles/profile.css";

const roleContent: Record<
  Role,
  {
    label: string;
    description: string;
    icon: typeof ShieldCheck;
  }
> = {
  admin: {
    label: "Pentadbir",
    description: "Akses keseluruhan sekolah, laporan dan tetapan.",
    icon: ShieldCheck,
  },
  teacher: {
    label: "Guru kelas",
    description: "Akses imbasan, kelas tugasan dan rekod berkaitan.",
    icon: GraduationCap,
  },
};

export function ProfilePage() {
  const { session, students, classes, login, logout } = useApp();
  const [standalone, setStandalone] = useState(false);

  useEffect(() => {
    const media = window.matchMedia("(display-mode: standalone)");
    const updateMode = () => setStandalone(media.matches);
    updateMode();
    media.addEventListener?.("change", updateMode);
    return () => media.removeEventListener?.("change", updateMode);
  }, []);

  const assignedClass = session?.assignedClassId
    ? classes.find((schoolClass) => schoolClass.id === session.assignedClassId)
    : undefined;

  const scope =
    session?.role === "admin"
      ? {
          label: "Seluruh sekolah",
          detail: `${classes.length} kelas · ${students.length} murid`,
          icon: School,
        }
      : {
          label: assignedClass?.name ?? "Kelas tugasan",
          detail: assignedClass
            ? `${assignedClass.studentCount} murid · ${assignedClass.room}`
            : "Akses mengikut kelas yang ditetapkan",
          icon: Users,
        };

  return (
    <div className="profile-page">
      <PageHeader
        eyebrow="Akaun demo"
        title="Profil & sesi"
        description="Semak identiti aktif dan tukar peranan untuk menunjukkan pengalaman setiap pengguna."
      />

      <div className="profile-layout">
        <section className="profile-identity panel">
          <div className="profile-identity__crest">
            <SchoolMark compact />
          </div>
          <div className="profile-identity__avatar-wrap">
            <Avatar
              initials={session?.initials ?? "?"}
              size="xl"
              tone={session?.role === "teacher" ? 1 : 0}
              label={session?.name ?? "Tiada sesi aktif"}
            />
            <span
              className={`profile-presence ${
                session ? "profile-presence--active" : ""
              }`}
              aria-label={session ? "Sesi aktif" : "Tiada sesi"}
            />
          </div>

          {session ? (
            <>
              <h2>{session.name}</h2>
              <p className="profile-identity__title">{session.title}</p>
              <div className="profile-identity__badges">
                <span className={`profile-role-chip profile-role-chip--${session.role}`}>
                  {session.role === "admin" ? (
                    <ShieldCheck size={15} />
                  ) : (
                    <GraduationCap size={15} />
                  )}
                  {roleContent[session.role].label}
                </span>
                <StateBadge state="demo" />
              </div>

              <dl className="profile-details">
                <div>
                  <dt>ID pengguna</dt>
                  <dd>{session.id}</dd>
                </div>
                <div>
                  <dt>Skop akses</dt>
                  <dd>
                    <scope.icon size={16} />
                    <span>
                      <strong>{scope.label}</strong>
                      <small>{scope.detail}</small>
                    </span>
                  </dd>
                </div>
              </dl>

              <Button
                variant="secondary"
                icon={LogOut}
                full
                onClick={logout}
              >
                Log keluar
              </Button>
            </>
          ) : (
            <div className="profile-identity__signed-out">
              <h2>Tiada sesi aktif</h2>
              <p>Pilih satu peranan demo untuk masuk semula.</p>
              <StateBadge state="demo" />
            </div>
          )}
        </section>

        <div className="profile-main">
          <section className="profile-role-panel panel">
            <div className="profile-role-panel__header">
              <div>
                <span className="eyebrow">Pandangan pengguna</span>
                <h2>Tukar peranan demo</h2>
                <p>
                  Gunakan pilihan ini semasa pembentangan untuk membandingkan
                  akses pentadbir dan guru kelas.
                </p>
              </div>
              {session && (
                <span className="profile-current-session">
                  <span /> Sesi aktif
                </span>
              )}
            </div>

            <div className="profile-role-options">
              {(Object.keys(roleContent) as Role[]).map((role) => {
                const content = roleContent[role];
                const Icon = content.icon;
                const selected = session?.role === role;
                const roleScope =
                  role === "admin"
                    ? `${classes.length} kelas · semua modul`
                    : "3 Amanah · modul kelas";
                return (
                  <button
                    key={role}
                    type="button"
                    className="profile-role-option"
                    aria-pressed={selected}
                    onClick={() => login(role)}
                  >
                    <span className="profile-role-option__icon" aria-hidden="true">
                      <Icon size={22} />
                    </span>
                    <span className="profile-role-option__copy">
                      <strong>{content.label}</strong>
                      <span>{content.description}</span>
                      <small>{roleScope}</small>
                    </span>
                    <span
                      className="profile-role-option__check"
                      aria-hidden="true"
                    >
                      {selected && <Check size={16} />}
                    </span>
                  </button>
                );
              })}
            </div>
          </section>

          <section className="profile-prototype panel">
            <div className="profile-prototype__header">
              <span className="profile-prototype__icon" aria-hidden="true">
                {standalone ? <Smartphone size={21} /> : <Laptop size={21} />}
              </span>
              <div>
                <span className="eyebrow">Versi mockup</span>
                <h2>Aplikasi web progresif</h2>
              </div>
              <span className="profile-mode-badge">
                {standalone ? "Mod aplikasi" : "Mod pelayar"}
              </span>
            </div>

            <div className="profile-prototype__grid">
              <div>
                <Smartphone size={18} />
                <span>
                  <strong>Mesra telefon</strong>
                  <small>Boleh dibuka seperti aplikasi pada skrin utama.</small>
                </span>
              </div>
              <div>
                <WifiOff size={18} />
                <span>
                  <strong>Data lokal</strong>
                  <small>Perubahan demo kekal dalam pelayar peranti ini.</small>
                </span>
              </div>
              <div>
                <UserRound size={18} />
                <span>
                  <strong>Identiti simulasi</strong>
                  <small>Tiada akaun sebenar atau kata laluan digunakan.</small>
                </span>
              </div>
            </div>

            <InlineNotice tone="warning" title="Belum sistem produksi">
              Mockup ini tidak disambungkan kepada cloud, WhatsApp, APDM atau
              pangkalan data sekolah. Semua nama dan rekod ialah data contoh
              untuk demonstrasi.
            </InlineNotice>
          </section>
        </div>
      </div>
    </div>
  );
}
